import {
  forwardRef,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { reportFieldValue } from "@/lib/forms/reportFieldValue";
import {
  FieldControl,
  FieldFooter,
  FieldLabel,
  FieldShell,
} from "../../field-ui";
import type { FormFieldInputProps, FormFieldRef } from "../../types";
import { DatePickerControl } from "./DatePickerControl";

interface DateInputProps extends FormFieldInputProps {
  placeholder: string;
  labelMuted?: boolean;
  className?: string;
  footertext?: string;
  onChange?: (value: string) => void;
  validation?: (value: string) => boolean;
  invalidMessage?: (value: string) => string;
  withTime?: boolean;
  min?: string;
  max?: string;
}

const DateInput = forwardRef<FormFieldRef, DateInputProps>((props, ref) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const [value, setValue] = useState(props.initialValue ?? "");
  const [validationState, setValidationState] = useState(true);

  const commitValue = (next: string) => {
    setValue(next);
    setValidationState(true);
    reportFieldValue(props.fieldName, next, props.onValueChange);
    props.onChange?.(next);
  };

  const validate = () => {
    if (!props.validation) return true;
    const result = props.validation(value);
    setValidationState(result);
    return result;
  };

  useImperativeHandle(ref, () => ({
    setValidationState,
    value,
    fieldId: props.fieldId,
    fieldName: props.fieldName,
    scrollToElement: (options) => {
      shellRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        ...options,
      });
    },
    validate,
    clear: () => commitValue(""),
  }));

  return (
    <FieldShell ref={shellRef} className={props.className}>
      <FieldLabel htmlFor={inputId} muted={props.labelMuted}>
        {props.placeholder}
      </FieldLabel>
      <FieldControl>
        <input type="hidden" name={props.fieldName} value={value} />
        <DatePickerControl
          id={inputId}
          value={value}
          onChange={commitValue}
          withTime={props.withTime}
          invalid={!validationState}
          min={props.min}
          max={props.max}
          onDateBlur={(draft) => {
            if (value || draft) validate();
          }}
          onTimeBlur={(draft) => {
            if (value || draft) validate();
          }}
        />
      </FieldControl>
      <FieldFooter>
        {!validationState ? (
          <span className="text-red-600" role="alert">
            {props.invalidMessage?.(value) || "Укажите корректную дату"}
          </span>
        ) : props.footertext ? (
          <span className="text-slate-500">{props.footertext}</span>
        ) : null}
      </FieldFooter>
    </FieldShell>
  );
});

DateInput.displayName = "DateInput";

export { DateInput };
export type { DateInputProps };
